import { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/utils/dashboard/apiError';
import type { AdminKiosk } from '@/types/dashboard/kiosk';
import { KioskFormModal } from './KioskFormModal';
import { useDeleteKiosk, useKiosks, useUpdateKiosk } from './useKiosks';

export function KiosksPage() {
  const { data: kiosks = [], isLoading, isError, error, refetch } = useKiosks();
  const updateKiosk = useUpdateKiosk();
  const deleteKiosk = useDeleteKiosk();

  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdminKiosk | null>(null);
  const [toDelete, setToDelete] = useState<AdminKiosk | null>(null);
  const [deleteError, setDeleteError] = useState<{ message: string; hasHistory: boolean } | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return kiosks;
    return kiosks.filter((k) =>
      [k.name, k.location, k.vendor_name, k.kiosk_serial_number, k.printer_model].some((f) =>
        f.toLowerCase().includes(q),
      ),
    );
  }, [kiosks, search]);

  const openForm = (k: AdminKiosk | null) => {
    setEditing(k);
    setFormOpen(true);
  };

  const toggleActive = async (k: AdminKiosk) => {
    try {
      await updateKiosk.mutateAsync({ id: k.id, payload: { is_active: !k.is_active } });
      toast.success(k.is_active ? 'Kiosk deactivated' : 'Kiosk activated');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleteError(null);
    try {
      await deleteKiosk.mutateAsync(toDelete.id);
      toast.success('Kiosk deleted');
      closeDelete();
    } catch (err) {
      // 409: the kiosk has print/payment history, so only deactivating is allowed
      const hasHistory = (err as { response?: { status?: number } })?.response?.status === 409;
      setDeleteError({ message: getApiErrorMessage(err), hasHistory });
    }
  };

  const closeDelete = () => {
    setToDelete(null);
    setDeleteError(null);
  };

  const deactivateInstead = async () => {
    if (!toDelete) return;
    try {
      await updateKiosk.mutateAsync({ id: toDelete.id, payload: { is_active: false } });
      toast.success('Kiosk deactivated');
      closeDelete();
    } catch (err) {
      setDeleteError({ message: getApiErrorMessage(err), hasHistory: false });
    }
  };

  const sizes = (k: AdminKiosk) =>
    [k.is_a4 && `A4 (${k.a4_count})`, k.is_letter && `Letter (${k.letter_count})`, k.is_legal && `Legal (${k.legal_count})`]
      .filter(Boolean)
      .join(', ');

  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h2 className="text-headline-lg text-on-surface mb-1">Kiosks</h2>
          <p className="text-body-md text-on-surface-variant mb-0">Register and manage your print kiosks.</p>
        </div>
        <button type="button" className="btn btn-primary tap-target" onClick={() => openForm(null)}>
          <i className="bi bi-plus-lg me-2" />
          Add kiosk
        </button>
      </div>

      <div className="card shadow-kiosk-sm">
        <div className="card-body">
          <div className="position-relative mb-3" style={{ maxWidth: 360 }}>
            <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-on-surface-variant" />
            <input
              type="search"
              className="form-control border-0 rounded-pill bg-surface-low ps-5"
              placeholder="Search by name, location, vendor or serial"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {isLoading && (
            <div className="text-center py-5 text-on-surface-variant">
              <span className="spinner-border spinner-border-sm me-2" />
              Loading kiosks…
            </div>
          )}

          {isError && (
            <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
              <span className="text-body-sm">{getApiErrorMessage(error, 'Could not load kiosks')}</span>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => refetch()}>
                Retry
              </button>
            </div>
          )}

          {!isLoading && !isError && filtered.length === 0 && (
            <div className="text-center py-5">
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-circle bg-surface-low text-primary mb-3"
                style={{ width: 56, height: 56 }}
              >
                <i className="bi bi-printer fs-4" />
              </span>
              <div className="text-headline-sm text-on-surface">
                {kiosks.length === 0 ? 'No kiosks yet' : 'No kiosks match your search'}
              </div>
              {kiosks.length === 0 && (
                <p className="text-body-md text-on-surface-variant">Add your first kiosk to get started.</p>
              )}
            </div>
          )}

          {filtered.length > 0 && (
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr className="text-label-sm text-on-surface-variant">
                    <th className="bg-surface-low border-0 rounded-start-3">Kiosk</th>
                    <th className="bg-surface-low border-0">Vendor</th>
                    <th className="bg-surface-low border-0">Printer</th>
                    <th className="bg-surface-low border-0">Rates (₹/page)</th>
                    <th className="bg-surface-low border-0">Paper</th>
                    <th className="bg-surface-low border-0">Status</th>
                    <th className="bg-surface-low border-0 rounded-end-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((k) => (
                    <tr key={k.id} className="text-body-sm text-on-surface">
                      <td>
                        <div className="fw-semibold">{k.name}</div>
                        <div className="text-on-surface-variant">{k.location}</div>
                        <div className="text-on-surface-variant">{k.kiosk_serial_number}</div>
                      </td>
                      <td>{k.vendor_name}</td>
                      <td>
                        <div>{k.printer_model}</div>
                      </td>
                      <td>
                        {k.rate_charge ? (
                          <>
                            <div>B/W {k.rate_charge.bw_rate_per_page}</div>
                            <div>Colour {k.rate_charge.color_rate_per_page}</div>
                            <div>Duplex {Math.round(Number(k.rate_charge.duplex_extra_charge) * 100)}% off</div>
                          </>
                        ) : (
                          <span className="text-on-surface-variant">Not set</span>
                        )}
                      </td>
                      <td>{sizes(k)}</td>
                      <td>
                        <span
                          className={`badge rounded-pill ${
                            k.is_active ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'
                          }`}
                        >
                          {k.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-end text-nowrap">
                        <button
                          type="button"
                          className="btn btn-link text-on-surface-variant p-1"
                          title={k.is_active ? 'Deactivate' : 'Activate'}
                          onClick={() => toggleActive(k)}
                          disabled={updateKiosk.isPending}
                        >
                          <i className={`bi ${k.is_active ? 'bi-toggle-on text-success' : 'bi-toggle-off'} fs-5`} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link text-on-surface-variant p-1"
                          title="Edit"
                          onClick={() => openForm(k)}
                        >
                          <i className="bi bi-pencil fs-6" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link text-danger p-1"
                          title="Delete"
                          onClick={() => {
                            setDeleteError(null);
                            setToDelete(k);
                          }}
                        >
                          <i className="bi bi-trash fs-6" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {formOpen && <KioskFormModal kiosk={editing} onClose={() => setFormOpen(false)} />}

      <Modal show={toDelete !== null} onHide={closeDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-headline-sm">Delete kiosk</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-body-md">
          Delete <strong>{toDelete?.name}</strong> ({toDelete?.kiosk_serial_number})? This removes the kiosk record.
          {deleteError && (
            <div className="alert alert-danger text-body-sm mt-3 mb-0" role="alert">
              {deleteError.message}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={closeDelete}>
            Cancel
          </button>
          {deleteError?.hasHistory && toDelete?.is_active ? (
            <button type="button" className="btn btn-primary" onClick={deactivateInstead} disabled={updateKiosk.isPending}>
              {updateKiosk.isPending ? 'Deactivating…' : 'Deactivate instead'}
            </button>
          ) : (
            !deleteError?.hasHistory && (
              <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleteKiosk.isPending}>
                {deleteKiosk.isPending ? 'Deleting…' : 'Delete'}
              </button>
            )
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
